import { App, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { MobileProjectReportCard } from '@/components/mobile/MobileProjectReportCard';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import type { ProjectDetail } from '@/types/project';
import type { ProjectReportRecord } from '@/types/project-report';
import { PROJECT_REPORT_MERGE_NOTICE } from '@/constants/phase4-r-lite';
import { useAuthStore } from '@/stores/auth.store';
import { canCreateProjectReport } from '@/utils/project-report-permissions';

interface ProjectReportListSectionProps {
  projectId: string;
  project: ProjectDetail;
  onOpenReport: (reportId: string) => void;
  /** Lite 阶段关闭独立提交入口 */
  showSubmitEntry?: boolean;
  onSubmitReport?: () => void;
  /** Lite：引导 PM 去写日报 */
  onGoDailyReport?: () => void;
}

export function ProjectReportListSection({
  projectId,
  project,
  onOpenReport,
  showSubmitEntry = false,
  onSubmitReport,
  onGoDailyReport,
}: ProjectReportListSectionProps) {
  const { message } = App.useApp();
  const currentUser = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<ProjectReportRecord[]>([]);

  const canSubmit = canCreateProjectReport(currentUser, project.projectManagerId);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await projectReportsApi.fetchProjectReportsByProject(projectId, {
        page: 1,
        pageSize: 50,
      });
      setReports(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目日报失败';
      message.error(text);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [message, projectId]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  return (
    <section className="mobile-section">
      <div className="mobile-section__header">
        <span className="mobile-section__title">项目日报</span>
        <span className="mobile-section__meta">{reports.length} 条</span>
      </div>

      {canSubmit && showSubmitEntry && onSubmitReport ? (
        <button
          type="button"
          className="mobile-btn mobile-btn--primary mobile-btn--block"
          style={{ marginBottom: 12 }}
          onClick={onSubmitReport}
        >
          提交项目日报
        </button>
      ) : null}

      {canSubmit && !showSubmitEntry ? (
        <div className="mobile-project-report-merge-hint">
          <p className="mobile-hint" style={{ margin: 0 }}>
            {PROJECT_REPORT_MERGE_NOTICE}
          </p>
          {onGoDailyReport ? (
            <button
              type="button"
              className="mobile-btn mobile-btn--ghost mobile-btn--block"
              style={{ marginTop: 8 }}
              onClick={onGoDailyReport}
            >
              去写日报
            </button>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : reports.length === 0 ? (
        <MobileEmpty message="暂无项目日报" />
      ) : (
        reports.map((report) => (
          <MobileProjectReportCard
            key={report.id}
            report={report}
            onClick={() => onOpenReport(report.id)}
          />
        ))
      )}
    </section>
  );
}
